<template>
    <section>
        <h1 class="mb-0 mt-5 mb-3 text-center">{{$t('homework.header')}}</h1>

        <v-row>
            <v-col :cols="hasRole('teacher') ? 8 : 10">
                <v-text-field
                        v-model="keyword"
                        :label="$t('global.search')"
                        hide-details
                >
                    <template v-slot:prepend-inner>
                        <svg v-svg="'icon-search'" size="15"></svg>
                    </template>
                </v-text-field>
            </v-col>

            <v-col cols="2" v-if="hasRole('teacher')">
                <v-select :items="[{team_name: $t('global.all'), team_id: 0}, ...HOMEWORK]"
                          :label="$t('homework.team')"
                          item-text="team_name"
                          item-value="team_id"
                          v-model="team_id"
                          @change="getData()"
                >
                    <template v-slot:append>
                        <svg v-svg="'icon-select'" size="16"></svg>
                    </template>
                </v-select>
            </v-col>

            <v-col cols="2">
                <v-select :items="[{course_name: $t('global.all'), course_id: 0}, ...HOMEWORK]"
                          :label="$t('homework.subject')"
                          item-text="course_name"
                          item-value="course_id"
                          v-model="subject_id"
                          @change="getData()"
                >
                    <template v-slot:append>
                        <svg v-svg="'icon-select'" size="16"></svg>
                    </template>
                </v-select>
            </v-col>
        </v-row>

        <v-data-table :headers="headersFiltered"
                      :items="HOMEWORK"
                      hide-default-footer
                      class="elevation-1 mt-13 mb-6"
                      mobile-breakpoint="0"
        >
            <template v-slot:item.id="props">
                {{props.index+1}}
            </template>

            <template v-slot:item.actions="props">
                <v-btn text class="px-0 mr-1" @click="[$refs.show.dialog = true, selectedHomework = props.item.id]">
                    <svg v-svg="'icon-show'" size="12" class="mr-1"></svg>
                    <span class="show">{{$t('global.show')}}</span>
                </v-btn>

                <v-btn text class="px-0 mx-2" @click="[$refs.edit.dialog = true, selectedHomework = props.item.id]" v-if="hasRole('teacher')">
                    <svg v-svg="'icon-edit'" size="12" class="mr-1"></svg>
                    <span class="green--text">{{$t('global.edit')}}</span>
                </v-btn>

                <v-btn text class="px-0 mx-1" @click="[$refs.delete.dialog = true, selectedHomework = props.item.id]" v-if="hasRole('teacher')">
                    <svg v-svg="'icon-delete'" size="12" class="mr-1"></svg>
                    <span class="red--text">{{$t('global.delete')}}</span>
                </v-btn>

                <v-btn text class="px-0 ml-2" @click="[$refs.attachTeacher.dialog = true, selectedHomework = props.item.id, selectedAttach = props.item.pathTeacher.length]" v-if="hasRole('teacher')">
                    <svg v-svg="'icon-attach'" size="12" class="mr-1"></svg>
                    <span class="attach" v-if="props.item.pathTeacher.length">{{$t('attach.attached')}}</span>
                    <span class="attach" v-else>{{$t('attach.attach')}}</span>
                </v-btn>

                <v-btn text class="px-0 mx-2"
                       :class="!props.item.pathTeacher.length ? 'cursor-default' : ''"
                       :href="props.item.pathTeacher" target="_blank"
                       @click="checkError(props.item.pathTeacher)"
                       v-if="hasRole('student')"
                >
                    <svg v-svg="'icon-download'" size="12" class="mr-1"></svg>
                    <span class="download">{{$t('global.download')}}</span>
                </v-btn>

                <v-btn text class="px-0 ml-1" @click="[$refs.attachStudent.dialog = true, selectedHomework = props.item.id, selectedAttach = props.item.path.length]" v-if="hasRole('student')">
                    <svg v-svg="'icon-attach'" size="12" class="mr-1"></svg>
                    <span class="attach" v-if="props.item.path.length">{{$t('attach.attached')}}</span>
                    <span class="attach" v-else>{{$t('attach.attach')}}</span>
                </v-btn>
            </template>

            <template v-slot:header.actions class="d-none"></template>
        </v-data-table>

        <v-btn color="green" width="52" height="52" class="create" fab fixed bottom @click="$refs.create.dialog = true" v-if="hasRole('teacher')">
            <svg v-svg="'icon-create'" size="21"></svg>
        </v-btn>

        <create ref="create"></create>
        <show ref="show" :homework_id="selectedHomework"></show>
        <edit ref="edit" :homework_id="selectedHomework"></edit>
        <delete ref="delete" :homework_id="selectedHomework"></delete>
        <attach-teacher ref="attachTeacher" :homework_id="selectedHomework" :is_attached="selectedAttach"></attach-teacher>
        <attach-student ref="attachStudent" :homework_id="selectedHomework" :is_attached="selectedAttach"></attach-student>
    </section>
</template>

<script>
    import {mapGetters} from 'vuex';
    import Create from '@/components/Homework/Create';
    import Show from '@/components/Homework/Show';
    import Edit from '@/components/Homework/Edit';
    import Delete from '@/components/Homework/Delete';
    import AttachStudent from '@/components/Homework/AttachStudent';
    import AttachTeacher from '@/components/Homework/AttachTeacher';
    export default {
        components: {
            Create,
            Show,
            Edit,
            Delete,
            AttachStudent,
            AttachTeacher,
        },

        data(){
            return {
                keyword: null,
                timeout: null,
                loadingRequest: false,
                selectedHomework: null,
                selectedAttach: Boolean,
                team_id: null,
                subject_id: null,
                headers: [
                    {text: '#ID', sortable: false, value: 'id'},
                    {text: this.$t('homework.title'), sortable: false, value: 'title'},
                    {text: this.$t('homework.team'), sortable: false, value: 'team_name'},
                    {text: this.$t('homework.subject'), sortable: false, value: 'course_name'},
                    {text: this.$t('homework.lesson'), sortable: false, value: 'lesson_name'},
                    {text: 'Actions', sortable: false, value: 'actions', align: 'right'}
                ],
            }
        },

        computed: {
            ...mapGetters(['HOMEWORK']),
            headersFiltered(){
                return this.hasRole('student') ?  this.headers.filter(item => item.value !== 'team_name') : this.headers;
            }
        },

        mounted() {
            this.getData();
            this.$on('getData', function () {
                this.getData();
            });
            if(this.hasRole('teacher')){
                this.$store.dispatch('GET_TEAMS');
                this.$store.dispatch('GET_SUBJECTS');
                this.$store.dispatch('GET_LESSONS');
            }
        },

        watch: {
            keyword() {
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => {
                    this.getData();
                }, 300);
            }
        },

        methods: {
            getData(){
                this.loadingRequest = true;
                this.$store.dispatch('GET_HOMEWORK', {params: {team_id: this.team_id, subject_id: this.subject_id, keyword: this.keyword}})
                    .then(() => this.loadingRequest = false);
            },

            checkError(path){
                if(!path.length){
                    this.$store.dispatch('alert', {type: 2, message: this.$t('attach.no_attachment')});
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "../../scss/pages/tablePages";
    @import "../../scss/pages/Homework/index";
</style>